import React from 'react';
import reactor from 'data/reactor';
import { Immutable } from 'nuclear-js';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import RuntimeActions from 'data/app-runtime/actions';
import StudentActions from 'data/student/actions';
import StudentGetters from 'data/student/getters';
import RuntimeGetters from 'data/app-runtime/getters';
import DataUtils from 'data/utils';
import FormattingUtils from 'utils/formatting';
import CommonValues from 'resources/common-values';
import SubjectTabContainer from './subject-tabs';
import Highlights from './highlights';

export default CreateReactClass({
    displayName: 'student-info',

    propTypes() {
        return {
            currentStudentBid: PropTypes.string.isRequired,
            studentTransitionAction: PropTypes.string,
            studentInfo: PropTypes.instanceOf(Immutable.List),
            selectedTermNumber: PropTypes.number,
            testEvents: PropTypes.instanceOf(Immutable.List),
            unsavedData: PropTypes.bool,
            selectedSubject: PropTypes.instanceOf(Immutable.Map),
            currentTerm: PropTypes.instanceOf(Immutable.Map),
            showHighlights: PropTypes.bool.isRequired,
            highlights: PropTypes.instanceOf(Immutable.Map),
        };
    },

    getInitialState() {
        return {
            togglingHighlights: false,
        };
    },

    componentDidMount() {
        // Ensuring that the term dropdown is reset when coming back from selection screen, or when selecting student from student list.
        // Also causes the selected tab to be evaluated (i.e. Spanish -> Math if Spanish doesn’t exist for student).
        // The setTimeout allows us to side-step the error of dispatching an action within an action.

        if (!window.location.pathname.endsWith(CommonValues.SINGLE_STUDENT_PATH)) {
            setTimeout(() => {
                StudentActions.changeSelectedTermNumber(
                    this.props.currentStudentBid,
                    CommonValues.mostRecentTermNumber
                );
            }, 1);
        }
    },

    getAdjacentStudentBid() {
        const adjacentStudent =
            this.props.studentTransitionAction == null
                ? null
                : this.props.studentTransitionAction == 'left'
                ? reactor.evaluate(StudentGetters.nextStudent(this.props.currentStudentBid))
                : reactor.evaluate(StudentGetters.previousStudent(this.props.currentStudentBid));
        const adjacentStudentBid = adjacentStudent == null ? null : adjacentStudent.get('studentBid', null);

        return adjacentStudentBid;
    },

    getStudentData(studentInfo, selectedTermNumber) {
        let selectedStudentInfo = null;
        if (selectedTermNumber == CommonValues.mostRecentTermNumber) {
            selectedStudentInfo = DataUtils.latestItemByTerm(studentInfo);
        } else {
            selectedStudentInfo = studentInfo
                ? studentInfo.find((si) => si.get('termNumber', '') == selectedTermNumber, null, null)
                : null;
        }
        if (__BATCH__ && !selectedStudentInfo) {
            selectedStudentInfo = DataUtils.latestItemByTerm(studentInfo);
        }
        const studentName = selectedStudentInfo ? FormattingUtils.studentFullNameWithInitial(selectedStudentInfo) : '';
        const studentGrade = selectedStudentInfo ? selectedStudentInfo.get('gradeNweaName', '') : '';
        const studentNumber = selectedStudentInfo ? selectedStudentInfo.get('number', '') : '';
        return { studentName, studentGrade, studentNumber };
    },

    finalizeTransition() {
        reactor.batch(() => {
            RuntimeActions.advanceToAdjacentStudent();
            /*
            Changing the selected term number for the adjacent student actually means changing it for the "new" current student.
            Reason: At the time that this 'finalizeTransition' function occurs, the studentBid hasn't changed yet (that's what
                 the RuntimeActions.advanceToAdjacentStudent() function above does).
            Previously, before single student rendering, we wanted to change the selected term on the outgoing student. This is
                 no longer needed since the outgoing student is removed from the DOM. Therefore, it is now preferable to use this
                 line to ensure that the incoming student has the Most Recent term showing, and that the correct tab is expanded.
            */
            const adjacentBid = this.getAdjacentStudentBid();
            if (adjacentBid) {
                /* TP BUG: https://nwea.tpondemand.com/entity/213644-reports-student-profile-spanish-reading-spanish 
                    Missing adjacentBid was causing changeSelectedTermNumber to find no test event data and therefore
                    moving selected subject tab to Math. 
               */
                StudentActions.changeSelectedTermNumber(
                    this.getAdjacentStudentBid(),
                    CommonValues.mostRecentTermNumber
                );
            }
        });
    },

    setTogglingClass() {
        this.setState({ togglingHighlights: true });
        setTimeout(() => this.setState({ togglingHighlights: false }), 1500);
    },

    toggleHighlights() {
        this.setTogglingClass();
        RuntimeActions.toggleHighlights();
    },

    render() {
        const { studentName, studentGrade, studentNumber } = this.getStudentData(
            this.props.studentInfo,
            this.props.selectedTermNumber
        );

        const togglingHighlightsClass = this.props.togglingHighlights ? 'toggling-highlights' : '';

        const slideDirection =
            this.props.studentTransitionAction == null ? '' : `slide-${this.props.studentTransitionAction}`;
        const adjacentStudentBid = this.getAdjacentStudentBid();
        let adjacentStudent = null;

        let classes = `student-info ${togglingHighlightsClass} ${slideDirection}`;
        const currentStudent = (
            <section className={classes} onAnimationEnd={this.finalizeTransition}>
                <div className="student-name">{studentName}</div>
                <div className="student-grade">Grade: {studentGrade}</div>
                <div className="student-number">ID: {studentNumber}</div>
                <SubjectTabContainer
                    studentBid={this.props.currentStudentBid}
                    testEvents={this.props.testEvents}
                    selectedTermNumber={this.props.selectedTermNumber}
                    highlights={this.props.highlights}
                    showHighlights={this.props.showHighlights}
                    toggleHighlightsFn={this.toggleHighlights}
                    unsavedData={this.props.unsavedData}
                    selectedSubject={this.props.selectedSubject}
                    currentTerm={this.props.currentTerm}
                />
                <Highlights
                    highlights={this.props.highlights}
                    selectedSubject={this.props.selectedSubject}
                    toggleHighlightsFn={this.setTogglingClass}
                />
            </section>
        );

        if (adjacentStudentBid) {
            const adjacentStudentInfo = reactor.evaluate(StudentGetters.studentInfo(adjacentStudentBid, false));
            const testEvents = reactor.evaluate(StudentGetters.testEventsForStudentBid(adjacentStudentBid));
            const highlights = reactor.evaluate(StudentGetters.highlightsForStudentBid(adjacentStudentBid));
            const { studentName, studentGrade, studentNumber } = this.getStudentData(
                adjacentStudentInfo,
                CommonValues.mostRecentTermNumber
            );
            classes = `student-info ${togglingHighlightsClass} ${slideDirection}`;
            adjacentStudent = !adjacentStudentBid ? null : (
                <section className={classes}>
                    <div className="student-name">{studentName}</div>
                    <div className="student-grade">Grade: {studentGrade}</div>
                    <div className="student-number">ID: {studentNumber}</div>
                    <SubjectTabContainer
                        studentBid={adjacentStudentBid}
                        testEvents={testEvents}
                        selectedTermNumber={CommonValues.mostRecentTermNumber}
                        highlights={highlights}
                        showHighlights={this.props.showHighlights}
                        toggleHighlightsFn={this.toggleHighlights}
                        unsavedData={this.props.unsavedData}
                        selectedSubject={this.props.selectedSubject}
                        currentTerm={this.props.currentTerm}
                    />
                </section>
            );
        }

        // When not transitioning between students, an empty 'student-info' element needs to be in the DOM in order for React to do its thing properly.
        // Without student info placeholders on either side of the currently viewed student, the transitions when advancing to the adjacent student wouldn't happen.
        // In other words, there needs to be a DOM element in place in order to transition it.
        // When batch printing, the 'student-info' elements need to be removed so that the CSS that color and position them works properly.
        //      Otherwise, the placeholder shows in the printout instead of the actual student information.
        const previousPlaceholder = __BATCH__ ? null : this.props.studentTransitionAction == 'right' ? (
            adjacentStudent
        ) : (
            <section className="student-info"></section>
        );
        const nextPlaceholder = __BATCH__ ? null : this.props.studentTransitionAction == 'left' ? (
            adjacentStudent
        ) : (
            <section className="student-info"></section>
        );

        return (
            <div id="student-info-container">
                {previousPlaceholder}
                {currentStudent}
                {nextPlaceholder}
            </div>
        );
    },
});
